import React, { FC } from 'react'
import { Redirect } from 'react-router-dom'
import LogoTransfix from 'transfix-ui/components/LogoTransfix'
import { Grid, Link, Typography, makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useGetDriverVerificationShipment } from 'apps/fleet-planner/types'
import { CheckFilledIcon, CheckOutlineIcon } from 'transfix-icons'
import { Spinner, brandColors } from 'transfix-ui'
import { DriverValidationErrorDisplay } from './DriverValidationErrorDisplay'
import { StopsSummary } from './stops-summary/StopsSummary'

export const useStyles = makeStyles({
  h4: {
    color: brandColors.green3,
  },
  body2: {
    padding: 0,
  },
  subtitle2: {
    color: brandColors.coolGray5,
  },
  carrierLabel: {
    color: brandColors.coolGray5,
  },
  checkmarkContainer: {
    backgroundColor: brandColors.green0,
    display: 'flex',
    alignItems: 'center',
    padding: '20px',
    border: `1px solid ${brandColors.green2}`,
    borderRadius: '4px 4px 0 0',
  },
  verifyTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderBottom: `1px solid ${brandColors.green2}`,
    borderRadius: '0 0 4px 4px',
    padding: '20px',
    marginBottom: '24px',
  },
  loadDetailsContainer: {
    marginBottom: '24px',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${brandColors.coolGray2}`,
    borderRadius: '4px',
  },
})

interface ILabelValueProps {
  label: string
  value?: string | null
}

const SpacedLabelValue = ({ label, value }: ILabelValueProps) => {
  const classes = useStyles()

  return (
    <Grid container>
      <Grid item md={4} xs={4}>
        <Typography variant='subtitle2' className={classes.subtitle2}>
          {label}
        </Typography>
      </Grid>
      <Grid item md={8} xs={8}>
        <Typography variant='body2' className={classes.body2} align='right'>
          {value || '-'}
        </Typography>
      </Grid>
    </Grid>
  )
}

export const DriverVerificationPage: FC = () => {
  const classes = useStyles()

  const { data, error, loading } = useGetDriverVerificationShipment({})
  const { share, shipment } = data || {}
  const {
    currentStopSequence,
    dispatcher,
    id: shipmentId,
    motorCarrier,
    stops = [],
    externalId,
    truck,
    trailer,
  } = shipment || {}
  const { dotNumber, id: motorCarrierId, mcNumber, name: carrierName } = motorCarrier || {}
  const { sharedWithDriver } = share || {}
  const { id: driverId, name: driverName } = sharedWithDriver || {}
  const { fullName: dispatcherName } = dispatcher || {}
  const { truckNumber } = truck || {}
  const { trailerNumber } = trailer || {}

  const currentStop = stops.find(stop => stop.stopSequence === (currentStopSequence || 1))
  // Ensure there are spaces after commas so the lines wrap after commas
  const neatPoReference = currentStop?.poNumber?.replaceAll(/,\s*/g, ', ')
  const neatBolReference = currentStop?.bolNumber?.replaceAll(/,\s*/g, ', ')

  const { trackEvent } = useFMSAnalytics()

  if (loading) {
    return <Spinner />
  }

  if (error) {
    if (error.status === 401) {
      trackEvent('Fraud', 'DRIVER_VERIFICATION_UNAUTHORIZED')
      return <Redirect to='/verify-driver-expired' />
    }
    trackEvent('Fraud', 'DRIVER_VERIFICATION_ERROR')
    return <DriverValidationErrorDisplay />
  }

  trackEvent('Fraud', 'DRIVER_VERIFICATION_SUCCESS', {
    shipmentId,
    motorCarrierId,
    driverId,
  })

  return (
    <Box display='flex' justifyContent='center'>
      <Box padding='32px 20px 20px 20px'>
        <Box boxShadow='0px 6px 12px rgba(0, 0, 0, 0.15)'>
          <Box className={classes.checkmarkContainer}>
            <CheckOutlineIcon size='xlarge' color='green2' />
            <Box marginLeft='12px'>
              <Typography variant='h4' className={classes.h4}>
                Load Details Verified
              </Typography>
            </Box>
          </Box>
          <Box className={classes.verifyTextContainer}>
            <LogoTransfix width={130} />
            <Box marginTop='18px'>
              <Typography variant='body2' className={classes.body2}>
                Avoid fraud and haul with confidence. Verify the load details match your load and
                confirm the website URL. Authentic URLs will always start with{' '}
                <b>apps.transfix.io</b>.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          <Box marginBottom='8px'>
            <Typography variant='subtitle2' className={classes.subtitle2}>
              Carrier
            </Typography>
          </Box>
          <Box marginBottom='8px'>
            <Typography variant='body2' className={classes.body2}>
              {carrierName}
            </Typography>
          </Box>
          <Box marginBottom='4px'>
            <Typography variant='body2' className={classes.body2}>
              DOT {dotNumber} · MC {mcNumber}
            </Typography>
          </Box>
          <Box display='flex' marginBottom='16px'>
            <Box marginRight='4px'>
              <CheckFilledIcon color='green2' size='medium' />
            </Box>
            <Typography variant='caption'>Verified</Typography>
          </Box>
          <Box marginBottom='16px'>
            <Typography variant='body2' className={classes.body2}>
              <Box component='span' className={classes.carrierLabel}>
                Driver:
              </Box>{' '}
              {driverName}
            </Typography>
          </Box>
          <Box marginBottom='24px'>
            <Typography variant='body2' className={classes.body2}>
              <Box component='span' className={classes.carrierLabel}>
                Dispatcher:
              </Box>{' '}
              {dispatcherName}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.loadDetailsContainer}>
          <Box bgcolor={brandColors.coolGray1} padding='8px 20px'>
            <Typography variant='overline'>Load #{externalId}</Typography>
          </Box>
          <Box padding='20px'>
            <Box marginBottom='16px'>
              <StopsSummary stops={stops} />
            </Box>
            <Box marginBottom='12px'>
              <SpacedLabelValue label='PO' value={neatPoReference} />
            </Box>
            <Box marginBottom='16px'>
              <SpacedLabelValue label='BOL' value={neatBolReference} />
            </Box>
            <Box borderTop={1} borderColor={brandColors.coolGray2} paddingY='16px'>
              <Box marginBottom='8px'>
                <SpacedLabelValue label='Truck #' value={truckNumber} />
              </Box>
              <SpacedLabelValue label='Trailer #' value={trailerNumber} />
            </Box>
            <Box bgcolor={brandColors.coolGray1} borderRadius='4px' padding='12px'>
              <Typography variant='caption'>
                If something doesn’t look correct, escalate to your facility manager or{' '}
                <Link href='tel:6469484480'>
                  <b className=''>Call Transfix</b>
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
